import { useCallback, useMemo } from 'react';
import { modals } from '@mantine/modals';
import { Text, Title } from '@mantine/core';
import AddTestsModal from '~/components/test/AddTestsModal';
import type { TestListItem } from '~/server/api/routers/test';
import { api } from '~/trpc-extra/api';
import type { TestSuiteFetchResult } from '~/server/api/routers/test-suite-router';

export const useSuiteAddTests = ({ tests, testSuite }: { tests: TestListItem[]; testSuite: TestSuiteFetchResult }) => {
  const userIds = useMemo(
    () => [...new Set(tests.map((test) => test.createdByUserId).filter((userId) => userId !== null))],
    [tests],
  );

  const { data: users } = api.team.getUsersInfo.useQuery({ userIds }, { enabled: userIds.length > 0 });

  const testData = useMemo(() => {
    return tests.map((item) => {
      return { ...item, createdByUserEmail: users?.[item.createdByUserId ?? '']?.email ?? '' };
    });
  }, [tests, users]);

  const testSuiteMemberSet = useMemo(() => new Set(testSuite.suiteTests.map((x) => x.testId)), [testSuite.suiteTests]);

  const testSkipMap = useMemo(
    () => Object.fromEntries(testSuite.suiteTests.map((x) => [x.testId, x.skip])),
    [testSuite.suiteTests],
  );

  const nonSuiteMemberTests = useMemo(
    () => testData.filter((x) => !testSuiteMemberSet.has(x.id)),
    [testData, testSuiteMemberSet],
  );
  const suiteMemberTests = useMemo(
    () =>
      testData
        .filter((x) => testSuiteMemberSet.has(x.id))
        .map((test) => ({
          ...test,
          skip: testSkipMap[test.id] || false,
        })),
    // Adding "tests" here cause infinite re-render.
    // testData is ok to be included, as it is stable and required to see updates to the test's properties (e.g. tags).
    [testSuiteMemberSet, testSkipMap, testData],
  );

  const addTestsToSuiteHandler = useCallback(() => {
    modals.open({
      title: (
        <div>
          <Title order={4}>Select Tests</Title>
          <Text
            size="sm"
            c="dimmed"
          >
            Select tests to add to <span className="font-bold">{testSuite.name}</span>.
          </Text>
        </div>
      ),
      size: 'auto',
      centered: true,
      styles: {
        body: {
          minHeight: '60vh',
        },
      },
      children: (
        <AddTestsModal
          tests={nonSuiteMemberTests}
          testSuiteId={testSuite.id}
        />
      ),
    });
  }, [testSuite, nonSuiteMemberTests]);

  return { addTestsToSuiteHandler, nonSuiteMemberTests, suiteMemberTests };
};
